import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import { useRef, useState } from "react";
import {
  apiRequest,
  calculateOffsetDate,
  catchErrorMessageHandler,
  displayDateFormat,
  getDayOfWeek,
} from "../../../infrastructure/backendService";
import * as Yup from "yup";
import { groupingStyles } from "../../ExpenseVoucher/ExpenseRequest/ExpenseRequest";
import {
  CommonButton,
  CommonOutlinedButton,
  CustomRequiredInputLabel,
} from "../../common/StyledComponents/StyledFields";
import CustomDatePickerField from "../../common/DatePicker/DatePicker";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";
import toast from "react-hot-toast";
import ErrorPopup from "../../common/ErrorPopup";
import apiPath from "../../../config/apiPaths.json";
import styles from "../../devoteeService/priestSchedule/priestSchedule.module.scss";
import dayjs from "dayjs";
import PageLoader from "../../common/PageLoader/PageLoader";
import { useReactToPrint } from "react-to-print";
import SchedulingBar from "../../devoteeService/priestSchedule/SchedulingScreen/SchedulingBar";

const printStyles = `
 @page {
      size: tabloid;
      margin: 24px;
    }
 
    /* Set layout to Landscape */
    body {
      width: 100vw;
      position: absolute;
    }
@media print {
  .printable {
    display: block !important;
  }
}
`;

const PriestDailySchedulesReport = () => {
  const componentRef = useRef<any>(null);

  const [isBtnLoading, setIsButtonLoading] = useState(false);
  const [priestsDailySchedule, setPriestsDailySchedule] = useState<any>(null);
  const [isAPICalled, setIsAPICalled] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);

  const initialValues = {
    startDate: displayDateFormat(new Date()),
  };

  const validationSchema = Yup.object().shape({
    startDate: Yup.string().required("From date is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit(values) {
      setIsButtonLoading(true);
      scheduleApiCall(values);
    },
    enableReinitialize: true,
  });

  const handleChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
  };

  const scheduleApiCall = async (values: any) => {
    await apiRequest({
      method: "GET",
      endpoint: apiPath.schedules,
      params: {
        scheduleType: "PRIEST",
        schedulesForDay: true,
        priestId: "",
        startDate: displayDateFormat(new Date(values?.startDate)),
      },
      includeToken: true,
    })
      .then((res: any) => {
        const data = res.data;
        const schedules = data?.map((each: any) => {
          return {
            ...each,
            date: { date: each?.startDate },
            schedules: each?.schedulesByDate,
          };
        });
        setPriestsDailySchedule(schedules);
        setIsButtonLoading(false);
        setIsAPICalled(true);
        setIsPageLoading(false);
      })
      .catch((err: any) => {
        setIsButtonLoading(false);
        setIsAPICalled(true);
        setIsPageLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />
        );
      });
  };

  const handlePreviousAndNextDay = (isPrevious = false) => {
    let startDate: any = new Date(formik.values.startDate);
    if (isPrevious) {
      startDate = calculateOffsetDate(startDate, -1);
    } else {
      startDate = calculateOffsetDate(startDate, 1);
    }
    startDate = dayjs(startDate);
    formik.setFieldValue("startDate", startDate);
    scheduleApiCall({ startDate });
    setIsPageLoading(true);
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: printStyles,
    documentTitle: `Priests daily schedules ${displayDateFormat(
      new Date(formik?.values?.startDate)
    )}`,
  });

  const renderFieldsComponent = () => {
    return (
      <Box className="common_box_shadow" sx={{ mt: "20px" }}>
        <Box sx={groupingStyles.groupedFieldsContainer}>
          <Box sx={groupingStyles.fieldContainer}>
            {CustomRequiredInputLabel("Date", true)}
            <CustomDatePickerField
              date={formik.values.startDate}
              handleDateChange={handleChange}
              errorMessage={
                formik.touched.startDate ? formik.errors.startDate : ""
              }
              name="startDate"
            />
          </Box>
        </Box>
        <CommonButton
          onClick={() => {
            !isBtnLoading && formik.handleSubmit();
          }}
          sx={{
            mt: `${
              formik.touched.startDate && formik.errors.startDate
                ? "20px"
                : "5px"
            }`,
          }}
        >
          <ButtonLoader isBtnLoading={isBtnLoading} btnText={"Submit"} />
        </CommonButton>
      </Box>
    );
  };

  const renderWeekAndDayButtons = () => {
    return (
      <Box
        px="15px"
        pt="15px"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: { xs: "flex-start", sm: "space-between" },
        }}
        className="no_print"
      >
        <Box sx={{display: "flex", flexWrap: "wrap", columnGap: "10px"}}>
          <CommonOutlinedButton
            className={styles.week_and_day_buttons}
            onClick={() => {
              handlePreviousAndNextDay(true);
            }}
          >
            {"< Previous Day >"}
          </CommonOutlinedButton>
          <CommonOutlinedButton
            className={styles.week_and_day_buttons}
            onClick={() => handlePreviousAndNextDay()}
          >
            {"< Next Day >"}
          </CommonOutlinedButton>
        </Box>
        <Box>
          <CommonOutlinedButton
            className={styles.week_and_day_buttons}
            onClick={handlePrint}
            sx={{ mt: { xs: "15px", sm: "0px" } }}
          >
            Print Report
          </CommonOutlinedButton>
        </Box>
      </Box>
    );
  };

  const renderScheduling = () => {
    return (
      <Box pb="24px">
        <Box
          sx={{
            display: "flex",
            mt: "15px",
            py: "8px",
            borderRadius: "0px !important",
          }}
          className={styles.date_and_schedule_title_container}
        >
          <Typography
            className={styles.date_and_schedule_title}
            sx={{
              px: { xs: "5px", md: "8px" },
              fontSize: { xs: "12px", md: "16px" },
              width: { xs: "75px", md: "114px" },
            }}
          >
            {"Priest Name"}
          </Typography>
          <Typography
            className={styles.date_and_schedule_title}
            sx={{ pl: "15px", fontSize: { xs: "12px", md: "16px" } }}
          >
            {"Day Schedules"}
          </Typography>
        </Box>
        {priestsDailySchedule?.map((pooja: any) => {
          return (
            <Box
              sx={{
                display: "flex",
                height: "fit-content",
                mt: "5px !important",
              }}
              className={styles.date_and_scheduling_bar_container}
            >
              <Typography
                sx={{
                  px: { xs: "5px", md: "8px" },
                  pt: "16px",
                  fontSize: { xs: "9px", md: "12px" },
                  width: { xs: "75px", md: "114px" },
                }}
                className={styles.date_text}
              >
                {pooja?.priestOrHallName}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  px: "15px",
                  pt: "4px",
                  pb: "9px",
                  background: "#FFECB4",
                }}
              >
                <SchedulingBar
                  handleClickAdd={(details) =>
                    // handleClickAdd({
                    //   ...details,
                    //   priestOrHallName: pooja?.priestOrHallName,
                    //   priestOrHallId: pooja?.priestOrHallId,
                    // })
                    {}
                  }
                  isOnlyDisplay={true}
                  priestSchedulesData={pooja}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderDailySchedules = () => {
    return (
      <>
        {priestsDailySchedule?.length > 0 ? (
          <Box
            className={styles.scheduling_container}
            ref={componentRef}
            sx={{ mt: "20px" }}
          >
            <Box className={styles.scheduling_title_container}>
              <Typography
                className={styles.scheduling_title}
                fontSize={{ xs: "15px", md: "23px" }}
              >
                {`Priest Daily Schedule (${getDayOfWeek(
                  new Date(formik?.values?.startDate)
                )} - ${displayDateFormat(
                  new Date(formik?.values?.startDate)
                )})`}
              </Typography>
            </Box>
            <>
              {renderWeekAndDayButtons()}
              {renderScheduling()}
            </>
          </Box>
        ) : (
          <Typography
            className="sub_heading"
            sx={{ fontSize: "18px !important" }}
          >
            There are no schedules for the given date.
          </Typography>
        )}
      </>
    );
  };

  return (
    <>
      <Typography className="sub_heading">Priests Daily Schedule</Typography>
      {renderFieldsComponent()}
      {isAPICalled && renderDailySchedules()}
      <PageLoader loading={isPageLoading} />
    </>
  );
};

export default PriestDailySchedulesReport;
